// Minimal client router over the History API: a path is the only owner of navigation state (decision 004).

import { useSyncExternalStore } from 'react';
import { hasUnsaved, leaveThen } from './unsaved.ts';

const listeners = new Set<() => void>();
const notify = () => { for (const l of listeners) l(); };
const here = () => location.pathname + location.search;
/** The address the app shows: «назад» over unsaved work is undone to it until the person answers. */
let shown: { href: string; state: unknown } | null = null;
let passBack = false;

/**
 * «Назад» and «вперёд» of the browser: the address has already changed when the event comes, so with unsaved work the shown page
 * is put back on top and the person is asked; on leaving, the step back goes to the page the person was heading to.
 */
export function onPopState(): void {
  if (passBack || !shown || !hasUnsaved()) { passBack = false; shown = { href: here(), state: history.state }; notify(); return; }
  const from = shown;
  history.pushState(from.state, '', from.href);
  leaveThen(() => { passBack = true; history.back(); });
}
if (typeof window !== 'undefined') { shown = { href: here(), state: history.state }; window.addEventListener('popstate', onPopState); }

/**
 * Navigation is instant; over unsaved work it waits for the person's answer (shell/unsaved.ts); motion belongs to the components (DESIGN.md «Движение»). Every entry made here is marked:
 * the record page's «назад» goes back only to a page of the app, and to the record's list after a direct visit.
 */
export function navigate(to: string, opts: { replace?: boolean; card?: boolean } = {}) {
  if (to === here()) return;
  const go = () => {
    // `card` marks a step that only laid a card on the stack (shell/cards.ts): closing that card is the browser's «назад».
    const state = { app: opts.replace ? !!(history.state as { app?: boolean } | null)?.app : true, ...(opts.card ? { card: true } : {}) };
    if (opts.replace) history.replaceState(state, '', to); else history.pushState(state, '', to);
    shown = { href: here(), state };
    notify();
  };
  // A replacement is the frame correcting its own address (a redirect, the saved view), not the person leaving: it is not asked about.
  if (opts.replace) go(); else leaveThen(go);
}
/** The current page was reached inside the app, so `history.back()` stays in it. */
export const cameFromApp = () => !!(history.state as { app?: boolean } | null)?.app;

export function useLocation(): { path: string; search: URLSearchParams } {
  const href = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => location.pathname + location.search);
  const u = new URL(href, location.origin);
  return { path: u.pathname, search: u.searchParams };
}

export type Route =
  | { name: 'home' }
  | { name: 'type'; typeKey: string; viewId: string | null }
  /** `moved` — the old address `/t/<type>/<id>` of the record panel: the frame replaces it with `/r/<id>`. */
  | { name: 'record'; recordId: string; moved?: boolean }
  | { name: 'automations'; id: string | null }
  /** The page of a pipeline (R-U31): its board, its list or its settings; `moved` — the old address `/pipeline/<id>` of the settings. */
  | { name: 'pipeline'; id: string; tab: 'board' | 'list' | 'gantt' | 'settings'; moved?: boolean }
  /** «Мои» of a section that lives in pipelines (R-U30): the person's records by the date of the next action, a board or a list. */
  | { name: 'mine'; typeKey: string; layout: 'board' | 'list' }
  | { name: 'screen'; key: string }
  | { name: 'settings'; section: string | null }
  | { name: 'notFound' };

export function parseRoute(path: string, search: URLSearchParams): Route {
  const p = path.split('/').filter(Boolean);
  if (!p.length) return { name: 'home' };
  if (p[0] === 't' && p[2]) return { name: 'record', recordId: p[2], moved: true };
  if (p[0] === 't' && p[1]) return { name: 'type', typeKey: p[1], viewId: search.get('view') };
  if (p[0] === 'r' && p[1]) return { name: 'record', recordId: p[1] };
  if (p[0] === 'p' && p[1]) { const tab = search.get('tab'); return { name: 'pipeline', id: p[1], tab: tab === 'list' || tab === 'settings' || tab === 'gantt' ? tab : 'board' }; }
  if (p[0] === 'pipeline' && p[1]) return { name: 'pipeline', id: p[1], tab: 'settings', moved: true };
  if (p[0] === 'm' && p[1]) return { name: 'mine', typeKey: p[1], layout: search.get('layout') === 'list' ? 'list' : 'board' };
  if (p[0] === 'a') return { name: 'automations', id: p[1] ?? null };
  if (p[0] === 's' && p[1]) return { name: 'screen', key: p[1] };
  if (p[0] === 'settings') return { name: 'settings', section: p[1] ?? null };
  return { name: 'notFound' };
}

/** A section of records, on a view. A record is always its own page `/r/<id>` (R-U6); the old `/t/<type>/<id>` redirects there. */
export const typeHref = (typeKey: string, viewId?: string | null) => `/t/${typeKey}${viewId ? `?view=${viewId}` : ''}`;
export const recordHref = (id: string) => `/r/${id}`;

/** Ctrl/⌘-click and middle click open links in a new tab like ordinary links (DESIGN.md «Запись»). */
export function wantsNewTab(e?: { metaKey?: boolean; ctrlKey?: boolean; button?: number } | null): boolean {
  return !!e && (!!e.metaKey || !!e.ctrlKey || e.button === 1);
}
